import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { useRoute } from 'shared/library/routes/route';
import React from 'react';
import { Platform, View } from 'react-native';
import { Breed, Animal } from 'shared/@types';
import { useTranslation } from 'react-i18next';
import { H1, H2 } from 'shared/components/title';
import { Text } from 'shared/components/text';
import { Link } from 'shared/components/link';
import { CharacteristicBadge } from 'shared/components/characteristic-badge';
import { Button } from 'shared/components/button';
import { PageBuilder } from 'shared/library/page-builder';
import { BreedCard } from 'shared/components/breed-card';
import { Image } from 'shared/components/image';
import { AdBanner } from 'shared/components/ad-banner';
import { AdAnimalProvider } from 'shared/components/ad-banner/ad-animal-context';
import { Breadcrumb } from 'shared/components/breadcrumb';
import { withNativeGradientFallback } from 'shared/library/native-gradient';
import { PageContent } from 'shared/components/page-content';
import { BreedComparisonTable } from 'shared/screens/breeds/breed-comparison-table';
import type { ApiRoute } from 'shared/library/api-route';
import {
  getCanonicalBreedComparisonResult,
  getRequestedBreedComparisonResult,
} from 'shared/library/canonical-breed-comparison';

interface ShowBreedComparisonScreenProps {
  breed: Breed;
  comparable: Breed;
  similarBreeds: Breed[];
}

const ShowBreedComparisonScreen = (props: ShowBreedComparisonScreenProps) => (
  <AdAnimalProvider
    animal={props.breed.animal === props.comparable.animal ? props.breed.animal : null}
  >
    <ShowBreedComparisonContent {...props} />
  </AdAnimalProvider>
);

const ShowBreedComparisonContent = ({
  breed,
  comparable,
  similarBreeds,
}: ShowBreedComparisonScreenProps) => {
  const { t } = useTranslation('screens');
  const route = useRoute();
  const heroImageSizes = '(max-width: 767px) calc((100vw - 24px) / 2), 384px';

  return (
    <PageContent nativeSafeTop={true} className={'!pt-0'}>
      <AdBanner className={'mb-6 mt-4 md:mt-6'} placement={'top'} />

      <Breadcrumb
        className={'mb-1.5'}
        items={[
          { text: t('breeds.show_breed.breeds'), href: route('breeds.index') },
          { text: breed.name, href: route('breeds.show', { breed: breed.slug }) },
          {
            text: t('breeds.index_comparisons.breadcrumb'),
            href: route('breeds.show.compare', { breed: breed.slug }),
          },
          { text: comparable.name, href: null },
        ]}
      />

      <H1
        title={t('breeds.compare_breeds.title', { breed1: breed.name, breed2: comparable.name })}
        subtitle={t('breeds.compare_breeds.description', {
          breed1: breed.name,
          breed2: comparable.name,
        })}
        hideTitleWhenNativeHeader={true}
      />

      <View className={'mx-auto mb-6 w-full max-w-3xl flex-row items-center'}>
        <View className={'flex-1'}>
          <Link
            href={route('breeds.show', { breed: breed.slug })}
            asBlock={true}
            containerClassName={'group items-center gap-3'}
          >
            <Image
              src={breed.avatar}
              alt={breed.name}
              containerClassName={'aspect-4/3 w-full'}
              imageClassName={'rounded-l-lg object-cover transition-opacity group-hover:opacity-75'}
              priority={true}
              sizes={heroImageSizes}
              sizeType={'card'}
            />
          </Link>

          <View
            pointerEvents={'none'}
            className={withNativeGradientFallback(
              'absolute bottom-0 left-0 right-0 z-10 rounded-bl-lg bg-gradient-to-b from-transparent to-black/50 py-3',
              'bg-black/40',
            )}
          >
            <Text className={'px-2 text-center text-sm font-bold text-white md:text-lg'}>
              {breed.name}
            </Text>
          </View>
        </View>

        <View
          pointerEvents={'none'}
          className={
            'absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white p-2 md:p-3'
          }
        >
          <Text className={'text-brand-brown-dark text-xl font-bold md:text-2xl'}>VS</Text>
        </View>

        <View className={'flex-1'}>
          <Link
            href={route('breeds.show', { breed: comparable.slug })}
            asBlock={true}
            containerClassName={'group items-center gap-3'}
          >
            <Image
              src={comparable.avatar}
              alt={comparable.name}
              containerClassName={'aspect-4/3 w-full'}
              imageClassName={'rounded-r-lg object-cover transition-opacity group-hover:opacity-75'}
              priority={true}
              sizes={heroImageSizes}
              sizeType={'card'}
            />
          </Link>

          <View
            pointerEvents={'none'}
            className={withNativeGradientFallback(
              'absolute bottom-0 left-0 right-0 z-10 rounded-br-lg bg-gradient-to-b from-transparent to-black/50 py-3',
              'bg-black/40',
            )}
          >
            <Text className={'px-2 text-center text-sm font-bold text-white md:text-lg'}>
              {comparable.name}
            </Text>
          </View>
        </View>
      </View>

      <BreedComparisonTable breed={breed} comparable={comparable} />

      <H2
        title={t('breeds.compare_breeds.characteristics_badges')}
        marginTop={Platform.OS === 'web'}
      />
      <View className={'mb-6 gap-6 md:flex-row'}>
        <View className={'flex-1'}>
          <Text className={'text-brand-brown-dark mb-3 text-lg font-bold'}>{breed.name}</Text>
          <View className={'flex-row flex-wrap gap-2'}>
            {breed.characteristics.map((characteristic) => (
              <CharacteristicBadge key={characteristic} characteristic={characteristic} />
            ))}
          </View>
        </View>
        <View className={'flex-1'}>
          <Text className={'text-brand-brown-dark mb-3 text-lg font-bold'}>{comparable.name}</Text>
          <View className={'flex-row flex-wrap gap-2'}>
            {comparable.characteristics.map((characteristic) => (
              <CharacteristicBadge key={characteristic} characteristic={characteristic} />
            ))}
          </View>
        </View>
      </View>

      <H2 title={t('breeds.compare_breeds.breed_summary')} marginTop={Platform.OS === 'web'} />
      <View className={'mb-6 gap-6 md:flex-row'}>
        <View className={'border-brand-brown-dark/15 flex-1 rounded-lg border bg-white p-4'}>
          <Text className={'text-brand-brown-dark mb-3 text-xl font-bold'}>
            {t('breeds.compare_breeds.about_breed', { breed: breed.name })}
          </Text>
          <Text className={'text-brand-brown-dark/70 mb-4'}>{breed.summary}</Text>
          <View className={'flex-row flex-wrap gap-3'}>
            <Button
              href={route('breeds.show', { breed: breed.slug })}
              text={t('breeds.compare_breeds.view_breed_page')}
              color={'brown'}
              dense
              icon={falAngleRight}
            />
            <Button
              href={route('breeds.show.listings', { breed: breed.slug })}
              text={t(
                breed.animal === Animal.Dog
                  ? 'breeds.compare_breeds.available_puppies'
                  : 'breeds.compare_breeds.available_kittens',
                { count: breed.active_pets_count },
              )}
              color={'brown'}
              dense
              icon={falAngleRight}
            />
          </View>
        </View>

        <View className={'border-brand-brown-dark/15 flex-1 rounded-lg border bg-white p-4'}>
          <Text className={'text-brand-brown-dark mb-3 text-xl font-bold'}>
            {t('breeds.compare_breeds.about_breed', { breed: comparable.name })}
          </Text>
          <Text className={'text-brand-brown-dark/70 mb-4'}>{comparable.summary}</Text>
          <View className={'flex-row flex-wrap gap-3'}>
            <Button
              href={route('breeds.show', { breed: comparable.slug })}
              text={t('breeds.compare_breeds.view_breed_page')}
              color={'brown'}
              dense
              icon={falAngleRight}
            />
            <Button
              href={route('breeds.show.listings', { breed: comparable.slug })}
              text={t(
                comparable.animal === Animal.Dog
                  ? 'breeds.compare_breeds.available_puppies'
                  : 'breeds.compare_breeds.available_kittens',
                { count: comparable.active_pets_count },
              )}
              color={'brown'}
              dense
              icon={falAngleRight}
            />
          </View>
        </View>
      </View>

      <H2
        title={t('breeds.compare_breeds.similar_breeds')}
        linkTitle={t('breeds.compare_breeds.explore_more_breeds')}
        linkHref={route('breeds.index')}
        marginTop={Platform.OS === 'web'}
      />
      <View
        className={
          Platform.OS === 'web'
            ? 'flex-cols-2 md:flex-cols-5 flex-row gap-3 md:gap-6'
            : 'flex-row flex-wrap gap-3'
        }
      >
        {similarBreeds.map((similarBreed) => (
          <View key={similarBreed.id} style={Platform.OS === 'web' ? undefined : { width: '48%' }}>
            <BreedCard breed={similarBreed} />
          </View>
        ))}
      </View>
    </PageContent>
  );
};

function breedComparisonApiRoute(breed: string, comparable: string): ApiRoute {
  return {
    method: 'get',
    url: `/breeds/${encodeURIComponent(breed)}/compare/${encodeURIComponent(comparable)}`,
  };
}

ShowBreedComparisonScreen.pageBuilder = new PageBuilder<
  ShowBreedComparisonScreenProps,
  { breed: string; comparable: string }
>()
  .request(async ({ req, params, ctx }) => {
    const requestedComparisonResult = getRequestedBreedComparisonResult({
      requestedBreedSlug: params.breed,
      requestedComparableSlug: params.comparable,
      locale: ctx.locale,
      hasWebResponse: Boolean(ctx.res),
    });

    if (requestedComparisonResult) {
      return requestedComparisonResult;
    }

    const data = await req<{
      breed: Breed;
      comparable: Breed;
      similar_breeds: Breed[];
    }>(breedComparisonApiRoute(params.breed, params.comparable));

    return {
      breed: data.breed,
      comparable: data.comparable,
      similarBreeds: data.similar_breeds,
    };
  })
  .transformProps(({ props, params, ctx }) => {
    const canonicalResult = getCanonicalBreedComparisonResult({
      breed: props.breed,
      comparable: props.comparable,
      requestedBreedSlug: params.breed,
      requestedComparableSlug: params.comparable,
      locale: ctx.locale,
      hasWebResponse: Boolean(ctx.res),
    });

    if (canonicalResult) {
      return canonicalResult;
    }

    return props;
  })
  .deriveRouteParams((props) => ({
    breed: props.breed.slug,
    comparable: props.comparable.slug,
  }));

export { ShowBreedComparisonScreen };
export type { ShowBreedComparisonScreenProps };
